/**
 * OVERVOLT — Puzzle Mode driver (Sprint 30 / puzzle-impl-s30).
 *
 * Three responsibilities, all built on top of the existing deterministic
 * simulation (no new simulation code, no new RNG):
 *
 *   1. {@link loadPuzzleLevel} — given a fresh `GameState` already in puzzle
 *      mode, write the level's path / wall mask / gold budget / allowed
 *      towers into it. Called from `Game.setPuzzleLevel`.
 *
 *   2. {@link evaluatePuzzleAttempt} — pure function `(level, attempt) →
 *      PuzzleOutcome`. Drives a fresh `GameState` through the existing
 *      `placeTower` / `startWave` / `simTick` pipeline (no `Game` instance
 *      needed — those are all pure mutators), then reports the result. Used
 *      by tests and the auto-solver QA tool.
 *
 *   3. {@link awardPuzzleBest} — pure update of the per-level bests record
 *      after a successful attempt. Pure: idempotent on duplicate calls;
 *      never regresses a best.
 *
 * Persistence for puzzle bests lives in its own `localStorage` blob
 * (`overvolt.puzzleBests`) so the existing `Profile` schema (`profile.ts`)
 * stays untouched in MVP — a future sprint can fold the bests into the
 * Profile blob if desired. The simulation reads neither blob; both are
 * UI-only meta state, on par with the existing Profile.
 *
 * See `docs/PUZZLE_DESIGN.md` §3 / §5.
 */
import { CONFIG } from '../config';
import { Rng } from '../engine/rng';
import {
  simTick,
  placeTower,
  setOverclock,
  startWave,
  upgradeTower,
  type Sfx,
} from './systems';
import { setActiveMap, activeMapId } from './map';
import { createInitialState, type GameState } from './state';
import {
  losWallsForLevel,
  puzzleMapId,
  type PuzzleBossDifficulty,
  type PuzzleLevel,
  type PuzzlePlacement,
} from './puzzles';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

/** A failure tag explaining why an attempt did not solve the level. */
export type PuzzleFailureReason =
  | 'leaked'              // lives ran out before the wave cleared
  | 'budget'              // gold spent exceeded the level's budget at some step
  | 'illegal-placement'   // a `placeTower` call returned false (wrong tile / forbidden tower)
  | 'unauthorized'        // a placement used a tower not in `allowedTowers`
  | 'timeout'             // the tick budget elapsed before the wave terminated
  | null;                 // null when the attempt solved

/** A pure summary of one evaluated attempt. */
export interface PuzzleOutcome {
  /** True when the wave cleared with `state.lives > 0`. */
  readonly solved: boolean;
  /** Why the attempt failed, or `null` when it solved. */
  readonly reason: PuzzleFailureReason;
  /** Logical ticks elapsed during `startWave` … terminal. */
  readonly ticksUsed: number;
  /** Lives remaining at termination — `level.startingLives` if solved cleanly. */
  readonly livesRemaining: number;
  /** Placeables planted (Pylons + weapon towers). */
  readonly towersUsed: number;
  /** Gold spent on placements + upgrades. */
  readonly goldSpent: number;
  /** Peak grid `demand` observed during the wave (par axis). */
  readonly peakDemand: number;
}

/** The persisted personal-best entry for one puzzle level — §5.2. */
export interface PuzzleBest {
  /** True the first time the level is solved; gate for the unlock chain. */
  solved: boolean;
  /** Best (lowest) tower count seen on a solving attempt. `Infinity` = none. */
  bestTowers: number;
  /** Best (lowest) gold spent on a solving attempt. */
  bestGold: number;
  /** Best (lowest) peak grid demand on a solving attempt. */
  bestPower: number;
  /** Best (lowest) tick count on a solving attempt. */
  bestTicks: number;
}

/** Persistence container keyed by `PuzzleLevel.id`. */
export type PuzzleBestsMap = Record<string, PuzzleBest>;

// ----------------------------------------------------------------------------
// Persistence — a separate localStorage blob from `overvolt.profile`.
// ----------------------------------------------------------------------------

/** Storage key for the puzzle-mode bests blob. Sibling of `overvolt.profile`. */
export const PUZZLE_BESTS_KEY = 'overvolt.puzzleBests';

/** Fresh "no attempts yet" best — every axis at +Infinity, `solved: false`. */
export function freshPuzzleBest(): PuzzleBest {
  return {
    solved: false,
    bestTowers: Number.POSITIVE_INFINITY,
    bestGold: Number.POSITIVE_INFINITY,
    bestPower: Number.POSITIVE_INFINITY,
    bestTicks: Number.POSITIVE_INFINITY,
  };
}

/** Read the persisted bests blob (UI thread; never called by the sim). */
export function loadPuzzleBests(): PuzzleBestsMap {
  try {
    const raw = localStorage.getItem(PUZZLE_BESTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    // Coerce per-level entries — JSON cannot represent Infinity, so any
    // missing axis falls back to Infinity. `null` deserialises cleanly here.
    const out: PuzzleBestsMap = {};
    for (const [id, val] of Object.entries(parsed as Record<string, unknown>)) {
      if (!val || typeof val !== 'object') continue;
      const v = val as Partial<PuzzleBest>;
      out[id] = {
        solved: v.solved === true,
        bestTowers: numOrInf(v.bestTowers),
        bestGold: numOrInf(v.bestGold),
        bestPower: numOrInf(v.bestPower),
        bestTicks: numOrInf(v.bestTicks),
      };
    }
    return out;
  } catch {
    return {};
  }
}

function numOrInf(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : Number.POSITIVE_INFINITY;
}

/** Write the bests blob back (best-effort; quota failures are non-fatal). */
export function savePuzzleBests(bests: PuzzleBestsMap): void {
  try {
    // JSON cannot serialise Infinity — round-trip through `null` on write
    // and back to Infinity on read (see `numOrInf`).
    const safe: Record<string, Record<string, unknown>> = {};
    for (const [id, b] of Object.entries(bests)) {
      safe[id] = {
        solved: b.solved,
        bestTowers: Number.isFinite(b.bestTowers) ? b.bestTowers : null,
        bestGold: Number.isFinite(b.bestGold) ? b.bestGold : null,
        bestPower: Number.isFinite(b.bestPower) ? b.bestPower : null,
        bestTicks: Number.isFinite(b.bestTicks) ? b.bestTicks : null,
      };
    }
    localStorage.setItem(PUZZLE_BESTS_KEY, JSON.stringify(safe));
  } catch {
    /* quota / SSR — silently ignored, same as profile.ts */
  }
}

/**
 * Fold an `outcome` into a per-level best. Pure: idempotent on duplicate
 * calls (same outcome twice → identical map), never regresses an axis.
 * Returns a new `PuzzleBestsMap` rather than mutating in place so the caller
 * can compare references for "did anything change" UI hints.
 */
export function awardPuzzleBest(
  prev: PuzzleBestsMap,
  levelId: string,
  outcome: PuzzleOutcome,
): PuzzleBestsMap {
  if (!outcome.solved) return prev;
  const before = prev[levelId] ?? freshPuzzleBest();
  const after: PuzzleBest = {
    solved: true,
    bestTowers: Math.min(before.bestTowers, outcome.towersUsed),
    bestGold: Math.min(before.bestGold, outcome.goldSpent),
    bestPower: Math.min(before.bestPower, outcome.peakDemand),
    bestTicks: Math.min(before.bestTicks, outcome.ticksUsed),
  };
  // Reference-stable when no axis improved.
  if (
    before.solved &&
    before.bestTowers === after.bestTowers &&
    before.bestGold === after.bestGold &&
    before.bestPower === after.bestPower &&
    before.bestTicks === after.bestTicks
  ) {
    return prev;
  }
  return { ...prev, [levelId]: after };
}

// ----------------------------------------------------------------------------
// State loader
// ----------------------------------------------------------------------------

/**
 * Mutate `state` (already in puzzle mode) so it represents a fresh attempt
 * on `level`: re-binds the active map to the level's synthetic registry
 * entry, sets the path / map id mirror, the gold budget, the `losWalls`
 * mask, and clears every transient. The caller must construct the state via
 * `createInitialState(_, 'puzzle')` first.
 *
 * Pure side-effects: no RNG, no `localStorage`. Calling it twice on the same
 * `(state, level)` is idempotent (the second call no-ops where it would
 * write identical values; the only mutating call is `setActiveMap`, which
 * itself is idempotent for an already-active id).
 */
export function loadPuzzleLevel(state: GameState, level: PuzzleLevel): void {
  // Re-bind the active map to the puzzle's synthetic entry — the live
  // `PATH` / `_blockedKeys` lookups now reflect the puzzle's geometry
  // without any change to `map.ts`. Falls back silently if the level isn't
  // registered (`setActiveMap` → DEFAULT_MAP) but a well-formed boot has
  // already run the side-effect in `puzzles.ts`.
  setActiveMap(puzzleMapId(level.id));
  state.mapId = activeMapId();
  state.path = level.path;
  state.mazePathLength = level.path.length - 1;
  state.gold = level.goldBudget;
  state.losWalls = losWallsForLevel(level, CONFIG.grid.cols);
  state.puzzleLevelId = level.id;
  state.wave = 0;
  state.overload = 0;
  state.waveOverload = 0;
  // Sprint 30 — puzzle mode uses a small lives pool so any leak matters.
  // Defaults to 3; the level may override.
  const lives = level.startingLives ?? 3;
  state.lives = lives;
  state.startLives = lives;
  // Sprint 63 (puzzle-ux-briefing-s63) — every freshly loaded level opens
  // with the briefing card up. The orchestrator (`Game.ts`) clears the flag
  // when the player presses Start; the headless evaluator never reads it
  // (the briefing is a UI signal, not a sim input).
  state.puzzleBriefingPending = true;
}

// ----------------------------------------------------------------------------
// Headless attempt evaluator
// ----------------------------------------------------------------------------

/**
 * Silent {@link Sfx} sink — the evaluator never plays audio. Pure object,
 * shared across calls.
 */
const SILENT_SFX: Sfx = { play() { /* deliberate no-op */ } };

/** Default tick budget — 5 minutes at 10 tps. Bail rather than spin. */
const DEFAULT_MAX_TICKS = 3000;

/**
 * Sprint 48 — snapshot of the live `CONFIG.boss*` fields a level's
 * `difficulty` block can override. The evaluator writes the override values
 * before running the wave and restores the snapshot after — single-threaded
 * puzzle sim, deterministic. The snapshot covers every field
 * {@link PuzzleBossDifficulty} can address.
 */
interface BossConfigSnapshot {
  bossBaseHp: number;
  bossPhase2HpFrac: number;
  bossPhase3HpFrac: number;
  bossPhase2Adds: typeof CONFIG.bossPhase2Adds;
  bossPhase3Adds: typeof CONFIG.bossPhase3Adds;
  bossDrainInterval: typeof CONFIG.bossDrainInterval;
  bossDrainTelegraph: number;
  bossDrainDuration: number;
  bossDrainMult: number;
  bossAddSpawnLead: number;
  bossPhaseSpeed: typeof CONFIG.bossPhaseSpeed;
}

function snapshotBossConfig(): BossConfigSnapshot {
  return {
    bossBaseHp: CONFIG.bossBaseHp,
    bossPhase2HpFrac: CONFIG.bossPhase2HpFrac,
    bossPhase3HpFrac: CONFIG.bossPhase3HpFrac,
    bossPhase2Adds: CONFIG.bossPhase2Adds,
    bossPhase3Adds: CONFIG.bossPhase3Adds,
    bossDrainInterval: CONFIG.bossDrainInterval,
    bossDrainTelegraph: CONFIG.bossDrainTelegraph,
    bossDrainDuration: CONFIG.bossDrainDuration,
    bossDrainMult: CONFIG.bossDrainMult,
    bossAddSpawnLead: CONFIG.bossAddSpawnLead,
    bossPhaseSpeed: CONFIG.bossPhaseSpeed,
  };
}

/**
 * Apply the level's `difficulty` block onto the live `CONFIG.boss*` fields.
 * Mutates `CONFIG`; the caller MUST restore via {@link restoreBossConfig}
 * after the attempt completes. Pure assignment — no derived state touched.
 */
function applyBossOverrides(diff: PuzzleBossDifficulty): void {
  if (diff.bossBaseHp !== undefined) CONFIG.bossBaseHp = diff.bossBaseHp;
  if (diff.bossPhase2HpFrac !== undefined) CONFIG.bossPhase2HpFrac = diff.bossPhase2HpFrac;
  if (diff.bossPhase3HpFrac !== undefined) CONFIG.bossPhase3HpFrac = diff.bossPhase3HpFrac;
  if (diff.bossPhase2Adds !== undefined) CONFIG.bossPhase2Adds = diff.bossPhase2Adds;
  if (diff.bossPhase3Adds !== undefined) CONFIG.bossPhase3Adds = diff.bossPhase3Adds;
  if (diff.bossDrainInterval !== undefined)
    CONFIG.bossDrainInterval = [
      diff.bossDrainInterval[0],
      diff.bossDrainInterval[1],
      diff.bossDrainInterval[2],
    ];
  if (diff.bossDrainTelegraph !== undefined)
    CONFIG.bossDrainTelegraph = diff.bossDrainTelegraph;
  if (diff.bossDrainDuration !== undefined)
    CONFIG.bossDrainDuration = diff.bossDrainDuration;
  if (diff.bossDrainMult !== undefined) CONFIG.bossDrainMult = diff.bossDrainMult;
  if (diff.bossAddSpawnLead !== undefined)
    CONFIG.bossAddSpawnLead = diff.bossAddSpawnLead;
  if (diff.bossPhaseSpeed !== undefined)
    CONFIG.bossPhaseSpeed = [
      diff.bossPhaseSpeed[0],
      diff.bossPhaseSpeed[1],
      diff.bossPhaseSpeed[2],
    ];
}

function restoreBossConfig(snap: BossConfigSnapshot): void {
  CONFIG.bossBaseHp = snap.bossBaseHp;
  CONFIG.bossPhase2HpFrac = snap.bossPhase2HpFrac;
  CONFIG.bossPhase3HpFrac = snap.bossPhase3HpFrac;
  CONFIG.bossPhase2Adds = snap.bossPhase2Adds;
  CONFIG.bossPhase3Adds = snap.bossPhase3Adds;
  CONFIG.bossDrainInterval = snap.bossDrainInterval;
  CONFIG.bossDrainTelegraph = snap.bossDrainTelegraph;
  CONFIG.bossDrainDuration = snap.bossDrainDuration;
  CONFIG.bossDrainMult = snap.bossDrainMult;
  CONFIG.bossAddSpawnLead = snap.bossAddSpawnLead;
  CONFIG.bossPhaseSpeed = snap.bossPhaseSpeed;
}

/**
 * Headlessly evaluate `attempt` against `level` and return a pure outcome.
 *
 * The evaluator constructs a private `GameState` (no `Game` orchestrator
 * needed — `simTick` / `placeTower` / `startWave` are all pure mutators), so
 * calling it does NOT disturb the live game. Determinism: identical
 * `(level, attempt)` yields the identical `PuzzleOutcome` on every host —
 * the only state that crosses calls would be the `Rng` cursor, which we
 * freshly seed per call.
 *
 * Failure modes are checked in placement order, then simulation order:
 *   1. Disallowed tower → `'unauthorized'`.
 *   2. `placeTower` returns false → `'illegal-placement'`.
 *   3. Tick budget exceeded → `'timeout'`.
 *   4. `state.lives <= 0` → `'leaked'`.
 *   5. Wave cleared with lives remaining → solved.
 */
export function evaluatePuzzleAttempt(
  level: PuzzleLevel,
  attempt: readonly PuzzlePlacement[],
  maxTicks: number = DEFAULT_MAX_TICKS,
): PuzzleOutcome {
  // Sprint 48 (puzzle-extra-s48): apply the level's `difficulty` block onto
  // the live `CONFIG.boss*` fields for the duration of this attempt. P7
  // Kernel Echo uses this to lock the post-Sprint-42 Kernel spec so a
  // future Kernel re-tune cannot silently break the level. Restore-via-
  // finally keeps the test API / live game's CONFIG byte-for-byte
  // identical after the call returns.
  const bossSnap = level.difficulty ? snapshotBossConfig() : null;
  if (level.difficulty) applyBossOverrides(level.difficulty);
  try {
    return runAttempt(level, attempt, maxTicks);
  } finally {
    if (bossSnap) restoreBossConfig(bossSnap);
  }
}

/**
 * Inner runner — extracted so the snapshot/restore boundary in
 * {@link evaluatePuzzleAttempt} sits at the function edge and a thrown
 * (typecheck / sim error) cannot leak the override into the live `CONFIG`.
 */
function runAttempt(
  level: PuzzleLevel,
  attempt: readonly PuzzlePlacement[],
  maxTicks: number,
): PuzzleOutcome {
  // Fresh state in puzzle mode. The seed is fixed at 0 — every puzzle-mode
  // simulation that uses RNG (only Fork spawn ordering today) is identical.
  const state = createInitialState('normal', 'puzzle');
  loadPuzzleLevel(state, level);
  // Sprint 48 — `difficulty.bossWave` lifts the boss's HP-curve input above
  // the puzzle baseline (0). The Kernel reads `state.wave` at spawn time
  // via `createBoss` → `hpMultiplier(wave)`, so writing it here before
  // `startWave` propagates into the boss's `maxHp`. Non-boss puzzles leave
  // `state.wave` at 0 (no enemy uses it for any other axis).
  if (level.difficulty?.bossWave !== undefined) {
    state.wave = level.difficulty.bossWave;
  }
  const rng = new Rng(0);

  let towersUsed = 0;
  const allowed = new Set(level.allowedTowers);

  // Apply each placement (and upgrades, if requested) in order.
  for (const p of attempt) {
    if (!allowed.has(p.type)) {
      return {
        solved: false,
        reason: 'unauthorized',
        ticksUsed: 0,
        livesRemaining: state.lives,
        towersUsed,
        goldSpent: level.goldBudget - state.gold,
        peakDemand: state.power.demand,
      };
    }
    const goldBefore = state.gold;
    if (!placeTower(state, p.type, p.col, p.row, SILENT_SFX)) {
      return {
        solved: false,
        reason: 'illegal-placement',
        ticksUsed: 0,
        livesRemaining: state.lives,
        towersUsed,
        goldSpent: level.goldBudget - state.gold,
        peakDemand: state.power.demand,
      };
    }
    towersUsed += 1;
    const targetLevel = p.level ?? 1;
    for (let i = 1; i < targetLevel; i += 1) {
      if (!upgradeTower(state, p.col, p.row, SILENT_SFX)) {
        return {
          solved: false,
          reason: 'illegal-placement',
          ticksUsed: 0,
          livesRemaining: state.lives,
          towersUsed,
          goldSpent: level.goldBudget - goldBefore,
          peakDemand: state.power.demand,
        };
      }
    }
  }

  // Snapshot the gold spent on placements before the wave runs.
  const goldSpentOnLayout = level.goldBudget - state.gold;

  // Start the authored wave and tick to a terminal condition. We splice the
  // level's wave directly into `state.activeWave` after calling `startWave`
  // so we don't rely on the genWave / CONFIG.waves table — puzzle waves are
  // authored data, not procedural.
  startWave(state, SILENT_SFX);
  state.activeWave = level.wave;
  // The wave director starts at group 0; `startWave` already reset it.
  state.director = { groupIndex: 0, spawnedInGroup: 0, cooldown: 0, done: false };

  let peakDemand = state.power.demand;
  let ticksUsed = 0;
  let reason: PuzzleFailureReason = null;
  while (ticksUsed < maxTicks) {
    simTick(state, rng, SILENT_SFX);
    ticksUsed += 1;
    // Sprint 48 — apply any Overclock toggles scheduled for this tick. The
    // schedule walks the (col, row) → tower lookup in placement order; an
    // entry referring to a missing tower (or a non-weapon tile) is a quiet
    // no-op via `setOverclock`'s return-false branch. Determinism-safe.
    if (level.overclockSchedule) {
      for (const ev of level.overclockSchedule) {
        if (ev.atTick !== ticksUsed) continue;
        const target = state.towers.find((t) => t.col === ev.col && t.row === ev.row);
        if (target) setOverclock(state, target.id, ev.on);
      }
    }
    if (state.power.demand > peakDemand) peakDemand = state.power.demand;
    if (state.lives <= 0) {
      reason = 'leaked';
      break;
    }
    // Puzzle win — director done and no enemies left. The legacy modes set
    // `state.phase = 'won'` on the campaign-length boundary; in puzzle mode
    // we read the same condition explicitly. (Wiring into `simTick` itself
    // is a follow-up sprint — see PUZZLE_DESIGN.md §3.3.)
    if (state.director.done && state.enemies.length === 0) {
      break;
    }
    // Defensive: if the simulation ended the wave on its own (campaignLength
    // reached for some reason), treat as solved.
    if (state.phase === 'won') break;
    if (state.phase === 'lost') {
      reason = 'leaked';
      break;
    }
  }

  if (reason === null && ticksUsed >= maxTicks) reason = 'timeout';

  const solved = reason === null;
  const goldSpentTotal = level.goldBudget - state.gold;
  return {
    solved,
    reason,
    ticksUsed,
    livesRemaining: state.lives,
    towersUsed,
    // `goldSpentTotal` may include refunds (none in MVP — selling is build-
    // phase only here); the placement-only figure is a useful debug aid.
    goldSpent: Math.max(goldSpentOnLayout, goldSpentTotal),
    peakDemand,
  };
}
